import { type ReactElement } from 'react';
import { Link, redirect, useLoaderData } from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  type CategorySummary,
  type ProviderProfileOwn,
  type ProviderSubscription,
} from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import { loadMySubscription } from '../features/billing/billing.js';
import { SubscriptionAccountLine } from '../features/billing/SubscriptionAccountLine.js';
import { ProviderForm, type ProviderFormActionData } from '../features/providers/ProviderForm.js';
import { parseProviderForm } from '../features/providers/signup.js';
import { ApiError } from '../shared/api-error.js';
import { loadCategories } from '../shared/categories.js';
import { queryKeys, routeContext } from '../shared/query.js';
import { loadSession } from '../shared/session.js';

/**
 * `/:lang/account/pro` — a provider's own profile: every field they entered, and the same form to
 * change it. `W2-T05` §7.
 *
 * `GET /api/providers/me` is the owner's projection, so the street and postcode a visitor never sees
 * are here to correct. Saving is `PUT` with the whole document and **no coordinates**: the server
 * places the postcode, and keeps the stored point when the address lines are unchanged — so changing
 * a radius or a trade never moves anybody.
 *
 * Under `/account` because it is the account's, and behind the same guard. An account with no
 * profile has nothing to edit and is sent to `become-a-pro`, which is where one is made.
 */
interface AccountProData {
  locale: string;
  own: ProviderProfileOwn;
  categories: CategorySummary[];
  /** `W5-T13` §4.6 — for the status line; `null` (a failed read) is no line. */
  subscription: ProviderSubscription | null;
}

export async function loader({ params, context }: LoaderFunctionArgs): Promise<AccountProData> {
  const locale = localeOf(params);

  const session = await loadSession(context);
  if (session === null) throw redirect(`/${locale}/login`);

  const { queryClient, api } = context.get(routeContext);
  // `fetchQuery`: after a save this must show what was saved (`MEM-2026-09-14-3`).
  const own = await queryClient.fetchQuery({
    queryKey: queryKeys.myProviderProfile(),
    queryFn: () => api.getMyProviderProfile(),
  });
  if (own === null) throw redirect(`/${locale}/become-a-pro`);

  const [categories, subscription] = await Promise.all([
    loadCategories(context, locale),
    loadMySubscription(context),
  ]);
  return { locale, own, categories, subscription };
}

export async function action({
  params,
  request,
  context,
}: ActionFunctionArgs): Promise<ProviderFormActionData> {
  const locale = localeOf(params);
  const parsed = parseProviderForm(await request.formData());
  if (!parsed.ok) return { errors: parsed.errors };

  const { queryClient, api } = context.get(routeContext);

  let publicId: string;
  try {
    publicId = (await api.updateProviderProfile(parsed.input)).id;
  } catch (error) {
    // `W5-T07` §7: while a subscription is live the kind moves only with its price, from Billing.
    if (error instanceof ApiError && error.message === 'SUBSCRIPTION_KIND_LOCKED') {
      return { errors: { kind: 'billing.error.kind_locked' } };
    }
    return { failure: 'proForm.error.saveFailed' };
  }

  // Their own view, their public page and their feed all changed — the feed because trades and
  // radius are what it is computed from. `refetchType: 'all'` for the reason every action here
  // gives (`MEM-2026-09-27-jobs-1`).
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.myProviderProfile(), refetchType: 'all' }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.provider(locale, publicId),
      refetchType: 'all',
    }),
    queryClient.invalidateQueries({ queryKey: queryKeys.jobFeed(), refetchType: 'all' }),
  ]);
  return { saved: true };
}

export function Component(): ReactElement {
  const { t } = useTranslation();
  const { locale, own, categories, subscription } = useLoaderData<AccountProData>();

  return (
    <article data-testid="account-pro">
      <p>
        <Link to={`/${locale}/account`}>{t('proForm.edit.back')}</Link>
      </p>
      <h1>{t('proForm.edit.title')}</h1>
      <p className="mp-lead">{t('proForm.edit.intro')}</p>
      {/* `W5-T13` §4.6: not entitled — not listed, cannot quote — and the way to fix it. */}
      <SubscriptionAccountLine subscription={subscription} locale={locale} />
      <p>
        <Link to={`/${locale}/pro/${own.id}`}>{t('proForm.edit.public')}</Link>
      </p>
      {/* `W5-T01` AC 44: the way back to onboarding for a provider who skipped or left it. */}
      <p>
        <Link to={`/${locale}/account/payouts`}>{t('payouts.account.link')}</Link>
      </p>
      {/* `W5-T07` AC 38: what they pay, and where they change plan, card or cancel. */}
      <p>
        <Link to={`/${locale}/account/billing`}>{t('account.billing.link')}</Link>
      </p>

      <ProviderForm
        categories={categories}
        initial={own}
        testId="provider-edit-form"
        submitLabel={t('proForm.edit.submit')}
        pendingLabel={t('proForm.edit.pending')}
        kindNote={t('proForm.edit.kindNote')}
      />
    </article>
  );
}
